import React from 'react';
import { Home, Images } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import Masonry from 'react-masonry-css';
import { masonryPhotoGridBreakpoints } from '@lib/masonryPhotoGridBreakpoints';
import { ScrollArea } from '@components/ui/scroll-area';
import { Button } from '@components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { api } from '@/trpc/react';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import Link from 'next/link';
import { PhotoPackSamplePhoto } from '@components/Theme/PhotoPackSamplePhoto';
import { useSession } from '@hooks/index';
import { Pencil } from 'lucide-react';
interface PhotoPackSamplesSheetProps {
  photoPackId: string;
  hideDefaultTrigger?: boolean;
  isSheetOpen?: boolean;
  onSheetOpenChange?: (open: boolean) => void;
  onCustomize?: (open: boolean) => void;
}

export function PhotoPackSamplesSheet({
  photoPackId,
  onCustomize,
  isSheetOpen,
  onSheetOpenChange
}: PhotoPackSamplesSheetProps) {
  const showcaseImagesQuery = api.theme.getShowCaseImages.useQuery({ themeId: photoPackId }, { enabled: isSheetOpen });

  const handleCustomize = (newOpen: boolean) => {
    onCustomize?.(newOpen);
  };

  const handleSheetOpenChange = (newOpen: boolean) => {
    onSheetOpenChange?.(newOpen);
  };

  const { user } = useSession();
  const isAdmin = user?.isAdmin;

  const LoadingSkeleton = () => (
    <div className="space-y-8 py-6">
      {/* Header Section */}
      <div className="flex flex-row items-center gap-4">
        {/* Photo Pack Cover Skeleton */}
        <Skeleton className="w-32 h-32 rounded-md" />

        {/* Title and Description Section */}
        <div className="flex flex-col gap-3 w-full">
          <Skeleton className="h-4 w-32" /> {/* Category */}
          <Skeleton className="h-8 w-72" /> {/* Title */}
          <Skeleton className="h-4 w-48" /> {/* Tags */}
          <Skeleton className="h-10 w-32 mt-2" /> {/* Button */}
        </div>
      </div>

      {/* Separator */}
      <Skeleton className="h-px w-full my-6" />

      {/* Samples Section */}
      <div className="mb-4">
        <Skeleton className="h-6 w-32" /> {/* "Samples" text */}
      </div>

      {/* Image Grid */}
      <Masonry
        breakpointCols={{ ...masonryPhotoGridBreakpoints, default: 4, 2560: 4, 1920: 4 }}
        className="flex w-auto -ml-4"
        columnClassName="pl-4 bg-clip-padding space-y-4"
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="w-full h-[300px] rounded-md" />
        ))}
      </Masonry>
    </div>
  );

  return (
    <div>
      <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent side="bottom" className="h-[90svh] max-h-[90svh] w-full">
          <ScrollArea className="h-full p-4 w-full">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-10 xl:px-20 w-full h-full">
              {showcaseImagesQuery.isPending ? (
                <LoadingSkeleton />
              ) : (
                <>
                  <SheetHeader>
                    <Breadcrumb className="mb-4">
                      <BreadcrumbList className="text-xs">
                        <BreadcrumbItem>
                          <BreadcrumbLink asChild>
                            <Link href="/">
                              <Home size={13} strokeWidth={2} />
                            </Link>
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbLink asChild>
                            <Link href="/packs/browse">Photo Packs</Link>
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        {showcaseImagesQuery.data?.theme?.tags?.[0] && (
                          <>
                            <BreadcrumbItem>
                              <BreadcrumbLink
                                href={`/headshot-types/${showcaseImagesQuery.data.theme.tags?.[0]}`}
                                className="capitalize"
                              >
                                {showcaseImagesQuery.data.theme.tags?.[0]}
                              </BreadcrumbLink>
                            </BreadcrumbItem>
                          </>
                        )}
                      </BreadcrumbList>
                    </Breadcrumb>
                    <div className="flex flex-row items-center justify-between gap-6 w-full">
                      <div className="flex flex-col gap-1 h-full w-full">
                        <SheetTitle className="md:text-3xl font-bold mb-0 leading-none text-left flex flex-row items-center gap-2">
                          {showcaseImagesQuery.data?.theme.name || 'Photo Pack Samples'}
                          {isAdmin && showcaseImagesQuery.data?.theme?.id && (
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() =>
                                window.open(`/admin/themes/${showcaseImagesQuery.data.theme.id}`, '_blank')
                              }
                              title="Go to Theme Admin"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="sr-only">Go to Theme Admin</span>
                            </Button>
                          )}
                        </SheetTitle>
                        <SheetDescription className="text-left">
                          {showcaseImagesQuery.data?.theme?.tags && showcaseImagesQuery.data.theme.tags.length > 0 && (
                            <>
                              Tags:
                              <span className="ml-2 capitalize">
                                {showcaseImagesQuery.data.theme.tags.map((tag: string, index: number) => (
                                  <React.Fragment key={tag}>
                                    <Link
                                      href={`/headshot-types/${tag}`}
                                      className="dark:text-blue-300 text-blue-600 hover:underline"
                                    >
                                      {tag}
                                    </Link>
                                    {index < showcaseImagesQuery.data.theme.tags.length - 1 && ', '}
                                  </React.Fragment>
                                ))}
                              </span>
                            </>
                          )}
                        </SheetDescription>
                      </div>
                      <Button
                        size="sm"
                        variant="accent"
                        onClick={() => {
                          handleCustomize(true);
                        }}
                        className="w-fit px-6 rounded-full font-semibold"
                      >
                        Get <span className="hidden md:inline">photo pack</span>
                      </Button>
                    </div>
                  </SheetHeader>

                  <div>
                    {!showcaseImagesQuery.data?.showCaseImages ||
                    showcaseImagesQuery.data?.showCaseImages.length === 0 ? (
                      <Alert className="text-center py-8 mb-10">
                        <div className="w-full flex justify-center items-center pb-6">
                          <Images />
                        </div>
                        <AlertTitle className="m-auto mb-2">
                          There aren&apos;t any photos highlighted for this pack yet.
                        </AlertTitle>
                        <AlertDescription className="m-auto">
                          We&apos;re still collecting examples to showcase here. Check back soon, or be the first to use
                          this pack and share your stunning portraits with us!
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        <Separator className="mb-6 bg-foreground/10" />
                        <div className="mb-4">
                          <h3 className="text-lg tracking-tight text-muted-foreground">Samples</h3>
                        </div>

                        <Masonry
                          breakpointCols={{ ...masonryPhotoGridBreakpoints, default: 4, 2560: 4, 1920: 4 }}
                          className="flex w-auto -ml-4"
                          columnClassName="pl-4 bg-clip-padding -mb-4"
                        >
                          {showcaseImagesQuery.data?.showCaseImages?.map(image => (
                            <PhotoPackSamplePhoto
                              key={image.id}
                              imageUrl={image.imageUrl}
                              id={image.id}
                              themeId={showcaseImagesQuery.data.theme.id}
                              className="mb-4 group hover:shadow-lg transition"
                              showAdminActions
                            />
                          ))}
                        </Masonry>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
}
